import type { Level as PinoLevel } from "pino";

import "amazon-connect-streams";
import { getLogger, type Logger } from "@expert/logging";

import { type ConnectLogLevel, FORWARDED_LEVEL_FIELD, LEVELS_BY_SEVERITY, STREAMS_SOURCE } from "./streamsLogging";

const PINO_LEVEL_METHOD = {
    TEST: "trace",
    TRACE: "trace",
    DEBUG: "debug",
    INFO: "info",
    LOG: "info",
    WARN: "warn",
    ERROR: "error",
    CRITICAL: "fatal",
} satisfies Record<ConnectLogLevel, PinoLevel>;

interface ForwardStreamsLogsOptions {
    /**
     * Entries below this streams level are dropped before they reach pino. Everything clearing it is
     * also POSTed to the log collector, since `env.logLevel.api` is `trace` in every deployed
     * environment — hence the conservative `WARN` that `createAgentSdk` falls back to.
     *
     * `INFO` is where the entries explaining a failed or slow CCP start live: iframe refresh
     * attempts, softphone manager and ringtone engine setup, global-resiliency failover.
     */
    minLevel: ConnectLogLevel;

    /** Defaults to a `module: "amazon-connect-streams"` child of the root logger. */
    logger?: Logger;

    /**
     * Silence streams' own console echo, leaving the console to our prettier output. Pass `false`
     * where our console output is off (`VITE_DISABLE_CONSOLE_LOGS`), or the two together leave the
     * browser console with no streams logging at all.
     */
    muteConsoleEcho?: boolean;
}

let installed = false;

/**
 * Forwards amazon-connect-streams log entries to our logger, and from there to the log collector.
 * Call before `initCCP` so the CCP's own init logging is covered.
 *
 * Installs once; later calls ignore their options and return. Never throws — it sits at the top of
 * CCP init, so failing to set up logging must not stop the CCP from starting.
 */
export default function forwardStreamsLogs(options: ForwardStreamsLogsOptions) {
    if (installed) return;

    try {
        install(options);
    } catch (error) {
        // `getLogger` throws until `initLogging` has run, so our own logger is a candidate for what
        // just failed; report it the one way that cannot depend on it.
        console.error("Failed to forward amazon-connect-streams logs", error);
    }
}

function install({ minLevel, logger, muteConsoleEcho = true }: ForwardStreamsLogsOptions) {
    const log = logger ?? getLogger({ module: STREAMS_SOURCE });
    const minSeverity = LEVELS_BY_SEVERITY.indexOf(minLevel);
    log.info({ minLevel, muteConsoleEcho }, "Forwarding amazon-connect-streams logs");
    // Patch the singleton, not `connect.Logger.prototype`: streams wraps each prototype method in a
    // try/catch and assigns the result onto the instance, so a prototype patch is never reached.
    const streamsLogger = connect.getLog();
    const ownLoggerId = streamsLogger.getLoggerId();

    // Unbound is intentional: the Proxy forwards whatever `this` the caller used.
    // oxlint-disable-next-line typescript/unbound-method
    streamsLogger.addLogEntry = new Proxy(streamsLogger.addLogEntry, {
        apply(target, thisArgument, argumentsList: [connect.LogEntry]) {
            // Streams first: it redacts relayed entries inside `addLogEntry`, so reading one before
            // it would ship unredacted text and objects.
            const result = Reflect.apply(target, thisArgument, argumentsList);
            const [entry] = argumentsList;

            // Then a microtask, because "after `addLogEntry`" is still too early: streams builds
            // entries fluently and attaches the interesting parts once the call returns —
            // `getLog().error("...").withException(e).withObject(data)` — so forwarding from here
            // ships `exception: null` and `objects: []`. Microtasks drain FIFO, so order holds.
            queueMicrotask(() => {
                try {
                    forward(log, entry, minSeverity, ownLoggerId);
                } catch {
                    // Losing a log line must never break CCP.
                }
            });

            return result;
        },
    });

    installed = true;

    if (muteConsoleEcho) {
        // `CRITICAL` is as quiet as `setEchoLevel` goes, and streams never logs at that level itself
        // (`Logger.critical` writes at `ERROR`), so this drops the duplicate and hides nothing.
        streamsLogger.setEchoLevel(connect.LogLevel.CRITICAL);
    }
}

function forward(logger: Logger, entry: connect.LogEntry, minSeverity: number, ownLoggerId: string) {
    const { objects, exception, loggerId, tabId } = entry;

    // Each CCP broadcasts its CRM entries to the others, so two tabs would each forward the other's
    // copy. `loggerId` alone cannot be the test: CCP-iframe and shared-worker entries carry a foreign
    // one too, and those are the point.
    if (loggerId !== ownLoggerId && entry.getContextLayer() === "CRM") return;

    const level = entry.getLevel();
    const severity = LEVELS_BY_SEVERITY.indexOf(level);
    const isKnownSeverity = severity !== -1;
    if (isKnownSeverity && severity < minSeverity) return;

    logger[PINO_LEVEL_METHOD[level] ?? "info"](
        {
            // `connect`-prefixed to clear pino's own `level` and `time`. A relayed time that did not
            // parse is `NaN` here, which serializes to `null` rather than throwing.
            [FORWARDED_LEVEL_FIELD]: level,
            connectTime: new Date(entry.getTime()),
            component: entry.getComponent(),
            contextLayer: entry.getContextLayer(),
            agentResourceId: entry.getAgentResourceId(),
            // Foreign on everything relayed from the CCP iframe and the shared worker.
            loggerId,
            ...(tabId ? { tabId } : {}),
            // Unredacted when the entry was logged in this frame, since `withException` attaches
            // after both of streams' redaction passes; relayed entries are redacted, since
            // `LogEntry.fromObject` assigns `exception` before `addLogEntry` runs. Streams uploads
            // only `softphone`-component entries to Amazon, so for the rest this is the first thing
            // sending an exception off the box.
            ...(exception ? { exception } : {}),
            ...(objects?.length ? { objects } : {}),
        },
        entry.getText(),
    );
}
